// Embed helpers — pure functions for running the portal inside a partner iframe.
// The message bridge that wires these to the stores lives in embedBridge.ts.

export type EmbedTheme = 'light' | 'dark'

// Messages a partner parent may send. Anything else is ignored.
export type ParentMessage =
  | { type: 'qtz:auth'; accessToken: string; expiresAt?: string; css?: string; routes?: string[]; theme?: EmbedTheme }
  | { type: 'qtz:config'; css?: string; routes?: string[]; theme?: EmbedTheme }
  | { type: 'qtz:logout' }

export const MAX_PARTNER_CSS_BYTES = 64 * 1024

export function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin access to window.top can throw in some browsers — that means we are framed.
    return true
  }
}

// ── Embed policy ──────────────────────────────────────────────────────────────
// Who may embed the portal: EMBED_ALLOWED_ORIGINS, served as /embed-config.json by nginx
// (container env / --env-file) or by the Vite dev server (.env). Fails closed.
//   "" / none → own origin only · "*" → any origin · "https://a.com,https://b.com" → those + own
// The portal's own origin is always allowed, matching nginx's frame-ancestors 'self'.

export type EmbedPolicy = { mode: 'off' } | { mode: 'any' } | { mode: 'list'; origins: string[] }

export function parsePolicy(value: string | undefined | null): EmbedPolicy {
  const parts = (value ?? '')
    .split(/[\s,]+/)
    .map((o) => o.trim().replace(/\/+$/, ''))
    .filter(Boolean)
  if (parts.length === 0 || (parts.length === 1 && parts[0] === 'none')) return { mode: 'off' }
  if (parts.includes('*')) return { mode: 'any' }
  return { mode: 'list', origins: parts }
}

let policy: EmbedPolicy = { mode: 'off' }

export function getEmbedPolicy(): EmbedPolicy {
  return policy
}

export async function loadEmbedPolicy(): Promise<EmbedPolicy> {
  let value: string | undefined
  try {
    const resp = await fetch(`${import.meta.env.BASE_URL}embed-config.json`, { cache: 'no-store' })
    // Anything non-JSON (e.g. an SPA fallback page) counts as "not configured".
    if (resp.ok && resp.headers.get('content-type')?.includes('application/json')) {
      const body = (await resp.json()) as { allowedOrigins?: unknown }
      if (typeof body.allowedOrigins === 'string') value = body.allowedOrigins
    }
  } catch {
    // unreachable → not configured
  }
  policy = parsePolicy(value)
  return policy
}

export function isOriginAllowed(origin: string): boolean {
  if (!/^https?:\/\//.test(origin)) return false // rejects "null" (sandboxed / file: parents)
  if (origin === window.location.origin) return true
  if (policy.mode === 'any') return true
  if (policy.mode === 'list') return policy.origins.includes(origin)
  return false
}

// postMessage target for the parent, or null when embedding is not allowed here.
// ancestorOrigins (Chromium/WebKit) is authoritative; referrer is the Firefox fallback.
export function parentTarget(): string | null {
  let origin: string | null = null
  if (window.location.ancestorOrigins?.length) {
    origin = window.location.ancestorOrigins[0]
  } else if (document.referrer) {
    try {
      origin = new URL(document.referrer).origin
    } catch {
      origin = null
    }
  }
  if (origin) return isOriginAllowed(origin) ? origin : null
  // Origin undetectable (e.g. no-referrer): only "any" may proceed. qtz:ready carries no data.
  return policy.mode === 'any' ? '*' : null
}

export function isTrustedMessage(event: MessageEvent): boolean {
  return event.source === window.parent && isOriginAllowed(event.origin)
}

export function parseParentMessage(data: unknown): ParentMessage | null {
  if (!data || typeof data !== 'object') return null
  const msg = data as Record<string, unknown>
  switch (msg.type) {
    case 'qtz:auth':
      return typeof msg.accessToken === 'string' && msg.accessToken ? (msg as ParentMessage) : null
    case 'qtz:config':
    case 'qtz:logout':
      return msg as ParentMessage
    default:
      return null
  }
}

export function sanitizeRoutes(routes: unknown): string[] | undefined {
  if (!Array.isArray(routes)) return undefined
  return routes.filter((r): r is string => typeof r === 'string' && r.startsWith('/'))
}

export function sanitizeTheme(theme: unknown): EmbedTheme | undefined {
  return theme === 'light' || theme === 'dark' ? theme : undefined
}

// null routes = no restriction. '/' matches only the dashboard, other prefixes match sub-paths.
export function isRouteAllowed(pathname: string, routes: readonly string[] | null): boolean {
  if (!routes) return true
  return routes.some((r) => {
    const prefix = r.replace(/\/+$/, '') || '/'
    return prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(prefix + '/')
  })
}

// ── CSS-level hiding ──────────────────────────────────────────────────────────
// Elements carry stable data-qtz-part / data-qtz-nav attributes; partners hide or
// restyle them with plain CSS. Two adopted stylesheets: our base rules (defaults +
// ?hide= shortcut) and the partner's own CSS, which comes last so it can override.

// Hidden by default when embedded — the partner owns sign-out.
const DEFAULT_HIDE = ['user-menu']

const PART_ID = /^[\w:/.-]+$/

function hideSelector(id: string): string | null {
  if (!PART_ID.test(id)) return null
  if (id.startsWith('nav:')) return `[data-qtz-nav=${JSON.stringify(id.slice(4))}]`
  // Hiding the sidebar also hides the header button that opens it.
  if (id === 'sidebar') return '[data-qtz-part="sidebar"],[data-qtz-part="menu-toggle"]'
  return `[data-qtz-part=${JSON.stringify(id)}]`
}

export function hideRules(ids: readonly string[]): string {
  const selectors = ids.map((id) => hideSelector(id.trim())).filter(Boolean)
  return selectors.length ? `${selectors.join(',')}{display:none!important}` : ''
}

export function hideIdsFromSearch(search: string): string[] {
  const raw = new URLSearchParams(search).get('hide')
  return raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : []
}

let baseSheet: CSSStyleSheet | null = null
let partnerSheet: CSSStyleSheet | null = null

function adopt() {
  const ours = new Set([baseSheet, partnerSheet])
  const others = Array.from(document.adoptedStyleSheets).filter((s) => !ours.has(s))
  document.adoptedStyleSheets = [...others, ...[baseSheet, partnerSheet].filter((s): s is CSSStyleSheet => !!s)]
}

export function applyBaseCss(extraHideIds: readonly string[]) {
  baseSheet ??= new CSSStyleSheet()
  baseSheet.replaceSync(hideRules([...DEFAULT_HIDE, ...extraHideIds]))
  adopt()
}

// Returns false when the CSS was rejected (too large or unparsable).
// replaceSync ignores @import, so partner CSS cannot pull in remote stylesheets.
export function applyPartnerCss(css: string): boolean {
  if (new Blob([css]).size > MAX_PARTNER_CSS_BYTES) return false
  partnerSheet ??= new CSSStyleSheet()
  try {
    partnerSheet.replaceSync(css)
  } catch {
    return false
  }
  adopt()
  return true
}
